/**
 * Email Campaign Form Component (Issue #376)
 *
 * Allows Fair Managers to create and send promotional email campaigns to vendors.
 * Supports multiple targeting strategies and optional discount code integration.
 */

import React, { useState } from 'react';
import { fairManagerApi } from '../services/api';
import {
  EmailCampaignCreate,
  EmailCampaignTargetType,
  EmailCampaignResponse,
} from '../types';
import { Send, X, AlertCircle, CheckCircle } from 'lucide-react';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling

interface EmailCampaignFormProps {
  fairId: number;
  fairName: string;
  discountCodes?: Array<{ id: number; code: string }>;
  onSuccess?: (campaign: EmailCampaignResponse) => void;
  onCancel?: () => void;
}

/**
 * Form for creating and sending email campaigns to vendors
 */
export const EmailCampaignForm: React.FC<EmailCampaignFormProps> = ({
  fairId,
  fairName,
  discountCodes = [],
  onSuccess,
  onCancel,
}) => {
  // Form state
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [targetType, setTargetType] = useState<EmailCampaignTargetType>('all_vendors');
  const [discountCodeId, setDiscountCodeId] = useState<number | undefined>(undefined);

  // Target filters state
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [productTypeInput, setProductTypeInput] = useState('');
  const [radiusMiles, setRadiusMiles] = useState<number>(50);
  const [customEmails, setCustomEmails] = useState<string[]>([]);
  const [customEmailInput, setCustomEmailInput] = useState('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Character limits
  const SUBJECT_MAX = 200;
  const MESSAGE_MAX = 5000;

  /**
   * Add product type to filter list
   */
  const handleAddProductType = () => {
    const trimmed = productTypeInput.trim();
    if (trimmed && !productTypes.includes(trimmed)) {
      setProductTypes([...productTypes, trimmed]);
      setProductTypeInput('');
    }
  };

  /**
   * Remove product type from filter list
   */
  const handleRemoveProductType = (type: string) => {
    setProductTypes(productTypes.filter((t) => t !== type));
  };

  /**
   * Add email to custom list
   */
  const handleAddCustomEmail = () => {
    const trimmed = customEmailInput.trim().toLowerCase();
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (trimmed && emailRegex.test(trimmed) && !customEmails.includes(trimmed)) {
      setCustomEmails([...customEmails, trimmed]);
      setCustomEmailInput('');
    }
  };

  /**
   * Remove email from custom list
   */
  const handleRemoveCustomEmail = (email: string) => {
    setCustomEmails(customEmails.filter((e) => e !== email));
  };

  /**
   * Validate form before submission
   */
  const validateForm = (): boolean => {
    if (!subject.trim()) {
      setError('Subject is required');
      return false;
    }
    if (subject.length > SUBJECT_MAX) {
      setError(`Subject must be ${SUBJECT_MAX} characters or less`);
      return false;
    }
    if (!message.trim()) {
      setError('Message is required');
      return false;
    }
    if (message.length > MESSAGE_MAX) {
      setError(`Message must be ${MESSAGE_MAX} characters or less`);
      return false;
    }

    // Target-specific validation
    if (targetType === 'product_types' && productTypes.length === 0) {
      setError('At least one product type is required for product type targeting');
      return false;
    }
    if (targetType === 'geographic' && (!radiusMiles || radiusMiles <= 0)) {
      setError('Radius must be greater than 0 for geographic targeting');
      return false;
    }
    if (targetType === 'custom_list' && customEmails.length === 0) {
      setError('At least one email is required for custom list targeting');
      return false;
    }

    return true;
  };

  /**
   * Submit campaign
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Build target filters based on target type
      let targetFilters = undefined;
      if (targetType === 'product_types') {
        targetFilters = { product_types: productTypes };
      } else if (targetType === 'geographic') {
        targetFilters = { radius_miles: radiusMiles };
      } else if (targetType === 'custom_list') {
        targetFilters = { custom_emails: customEmails };
      }

      const campaignData: EmailCampaignCreate = {
        subject: subject.trim(),
        message: message.trim(),
        target_type: targetType,
        discount_code_id: discountCodeId,
        target_filters: targetFilters,
      };

      const response = await fairManagerApi.createEmailCampaign(fairId, campaignData);

      setSuccess(true);
      if (onSuccess) {
        onSuccess(response);
      }

      // Reset form after short delay
      setTimeout(() => {
        setSubject('');
        setMessage('');
        setTargetType('all_vendors');
        setDiscountCodeId(undefined);
        setProductTypes([]);
        setRadiusMiles(50);
        setCustomEmails([]);
        setSuccess(false);
      }, 2000);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Create Email Campaign</h2>
          <p className="text-gray-600 mt-1">Fair: {fairName}</p>
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
            title="Cancel"
          >
            <X className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Success message */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-green-800">Campaign created and sent successfully!</span>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Subject */}
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
            Subject Line *
          </label>
          <input
            id="subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={SUBJECT_MAX}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="e.g., Reserve Your Booth Today - Special Discount Inside!"
          />
          <div className="text-xs text-gray-500 mt-1">
            {subject.length}/{SUBJECT_MAX} characters
          </div>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Email Message *
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={MESSAGE_MAX}
            rows={8}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Write your promotional message here..."
          />
          <div className="text-xs text-gray-500 mt-1">
            {message.length}/{MESSAGE_MAX} characters
          </div>
        </div>

        {/* Discount Code */}
        {discountCodes.length > 0 && (
          <div>
            <label htmlFor="discount-code" className="block text-sm font-medium text-gray-700 mb-1">
              Discount Code (Optional)
            </label>
            <select
              id="discount-code"
              value={discountCodeId || ''}
              onChange={(e) => setDiscountCodeId(e.target.value ? Number(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">No discount code</option>
              {discountCodes.map((code) => (
                <option key={code.id} value={code.id}>
                  {code.code}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Include a discount code in the email to track conversions
            </p>
          </div>
        )}

        {/* Target Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Audience *
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                value="all_vendors"
                checked={targetType === 'all_vendors'}
                onChange={(e) => setTargetType(e.target.value as EmailCampaignTargetType)}
                className="mr-2"
              />
              <span>All Active Vendors</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="product_types"
                checked={targetType === 'product_types'}
                onChange={(e) => setTargetType(e.target.value as EmailCampaignTargetType)}
                className="mr-2"
              />
              <span>Vendors by Product Type</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="geographic"
                checked={targetType === 'geographic'}
                onChange={(e) => setTargetType(e.target.value as EmailCampaignTargetType)}
                className="mr-2"
              />
              <span>Vendors within Geographic Radius</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="custom_list"
                checked={targetType === 'custom_list'}
                onChange={(e) => setTargetType(e.target.value as EmailCampaignTargetType)}
                className="mr-2"
              />
              <span>Custom Email List</span>
            </label>
          </div>
        </div>

        {/* Product Types Filter */}
        {targetType === 'product_types' && (
          <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Types
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={productTypeInput}
                onChange={(e) => setProductTypeInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddProductType())}
                placeholder="e.g., jewelry, pottery, etc."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={handleAddProductType}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {productTypes.map((type) => (
                <span
                  key={type}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-primary-dark rounded-full text-sm"
                >
                  {type}
                  <button
                    type="button"
                    onClick={() => handleRemoveProductType(type)}
                    className="text-secondary hover:text-primary-dark"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Geographic Radius Filter */}
        {targetType === 'geographic' && (
          <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
            <label htmlFor="radius" className="block text-sm font-medium text-gray-700 mb-2">
              Radius (miles)
            </label>
            <input
              id="radius"
              type="number"
              value={radiusMiles}
              onChange={(e) => setRadiusMiles(Number(e.target.value))}
              min="1"
              max="500"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-gray-500 mt-1">
              Target vendors within this radius of the fair location
            </p>
          </div>
        )}

        {/* Custom Email List */}
        {targetType === 'custom_list' && (
          <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Addresses
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="email"
                value={customEmailInput}
                onChange={(e) => setCustomEmailInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomEmail())}
                placeholder="vendor@example.com"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={handleAddCustomEmail}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {customEmails.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-primary-dark rounded-full text-sm"
                >
                  {email}
                  <button
                    type="button"
                    onClick={() => handleRemoveCustomEmail(email)}
                    className="text-secondary hover:text-primary-dark"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 pt-4 border-t border-gray-200">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                <span>Sending Campaign...</span>
              </>
            ) : (
              <>
                <Send className="h-5 w-5" />
                <span>Create & Send Campaign</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailCampaignForm;
